import Button from '@/components/Button';
import Tooltip from '@/components/Tooltip/Tooltip';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import FileUploader from '@/components/FileUploader/FileUploader';
import MiniLoader from '@/components/MiniLoader/MiniLoader';
import { ACCEPTED_EXTS } from '@/components/ManualDataUploader/ManualUploader.helpers';
import { DataUnitStatsItem } from '@/core/types';
import { APIEventsEnum, useSendEvent } from '@/hooks';
import UploadIcon from '@/icons/upload_arrow.svg?react';
import AlertIcon from '@/icons/alert-orange-circle.svg?react';
import BlockedIcon from '@/icons/blocked.svg?react';

interface Props {
  currStats: DataUnitStatsItem | undefined;
  isDataConfirmed: boolean;
  sendFilesOnSelect: (files: File[] | null) => void;
  isExportingFile: boolean;
  isProcessingFiles: boolean;
  openModal: () => void;
}

export const CorrectionUploadNotifier = ({
  currStats,
  isDataConfirmed,
  sendFilesOnSelect,
  isExportingFile,
  isProcessingFiles,
  openModal,
}: Props) => {
  const { t } = useTranslation();
  const { sendEvent } = useSendEvent();
  if (!currStats) return null;

  return (
    <div
      className={classNames(
        'mt-4 flex flex-nowrap items-start justify-between gap-x-4 rounded-2xl border px-4 py-3',
        {
          'bg-red-100': !!currStats?.errors_count && !isDataConfirmed,
          'bg-gray-400 ': isDataConfirmed,
        }
      )}
    >
      {isDataConfirmed ? (
        <div className='flex gap-x-1'>
          <BlockedIcon className='shrink-0' />
          <div>
            <p className='font-bold'>
              {t(
                'Data for this period has been confirmed, and the report is being prepared or completed'
              )}
            </p>
            <p>
              {currStats.errors_count
                ? t(
                    "Corrections can't be made, but invoices with errors are not included to filing"
                  )
                : currStats.warnings_count
                  ? t(
                      "Corrections can't be made. Invoices with notices are included to filing"
                    )
                  : t(
                      "Corrections can't be made. All invoices are included to filing."
                    )}
            </p>
          </div>
        </div>
      ) : (
        <div className='flex gap-x-1'>
          <AlertIcon className='h-6 w-6 shrink-0' />
          <div>
            {currStats?.errors_count ? (
              <>
                <p className='font-bold'>
                  {t(
                    'This file has errors that need to be resolved before proceeding'
                  )}
                </p>
                <p>
                  {t('Export them and correct')}{' '}
                  {t('Upload the file with the corrected documents back')}
                </p>
              </>
            ) : (
              <>
                <p className='font-bold'>
                  {t('This file has non-critical issues that nice-to-fix')}
                </p>
                <p>
                  {t('You can export them and correct')}{' '}
                  {t('Upload the file with the corrected documents back')}
                </p>
              </>
            )}
          </div>
        </div>
      )}
      <div className='flex shrink-0 items-start gap-x-2'>
        <Button
          onClick={openModal}
          className='flex shrink-0 items-center gap-x-2 !py-1'
        >
          {isExportingFile && <MiniLoader className='h-6 w-6' />}
          <span>
            {t(
              isExportingFile
                ? 'Exporting file'
                : !!currStats?.errors_count
                  ? 'Download errors'
                  : 'Download notices'
            )}
          </span>
        </Button>
        <div className='ml-auto'>
          {isProcessingFiles ? (
            <Button
              styling='outline'
              className='flex shrink-0 items-center gap-x-2 !py-1'
            >
              <MiniLoader className='h-6 w-6' />
              <span>{t('Upload corrections')}</span>
            </Button>
          ) : isDataConfirmed ? (
            <Tooltip
              content={
                <div className='text-center'>
                  <p>{t('You can’t upload corrections')}</p>
                  <p>{t('Data for this period is confirmed')},</p>
                  <p>{t('and the report is being prepared or completed')}</p>
                </div>
              }
            >
              <Button
                className='flex shrink-0 items-center gap-x-2 !border-gray-500 !py-1'
                disabled
              >
                <UploadIcon />
                <span>{t('Upload corrections')}</span>
              </Button>
            </Tooltip>
          ) : (
            <FileUploader
              onSelect={(e) => {
                sendEvent(APIEventsEnum.files_uploaded_from_document_tab, {
                  block: 'customer uploaded files from the Documents tab',
                });
                sendFilesOnSelect(e);
              }}
              name='manual-upload-file'
              accept={ACCEPTED_EXTS}
              className='Button--outline flex shrink-0 cursor-pointer items-center gap-x-2 !py-1'
            >
              <UploadIcon />
              <span>{t('Upload corrections')}</span>
            </FileUploader>
          )}
        </div>
      </div>
    </div>
  );
};
